import { Tabs } from '@mantine/core';
import {
    IconActivity,
    IconAlertTriangle,
    IconInfoCircle,
    IconReceipt,
    IconServer,
    IconUsers,
} from '@tabler/icons-react';
import { Button } from '@xver/components/Atoms';
import { useAppSelector } from '@xver/hooks/useAppSelector';
import usePageHeading from '@xver/hooks/usePageHeading';
import { useProjectQuery } from '@xver/hooks/useProjectQueries';
import { getErrorCode } from '@xver/utils/error';
import { parseProjectTab, ProjectTab, projectTabsFor } from '@xver/utils/projects';
import { ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router';

import ProjectBreadcrumbs from '../ProjectsPage/components/ProjectBreadcrumbs';
import { TextBadge } from '../ProjectsPage/components/ProjectBits';
import ActivityTabPanel from './components/ActivityTabPanel';
import BillingTabPanel from './components/BillingTabPanel';
import DangerZoneTabPanel from './components/DangerZoneTabPanel';
import GeneralTabPanel from './components/GeneralTabPanel';
import MembersTabPanel from './components/MembersTabPanel';
import ResourcesTabPanel, { ResourceSectionKey } from './components/ResourcesTabPanel';

const TAB_ICONS: Record<ProjectTab, ReactNode> = {
    general: <IconInfoCircle size={18} aria-hidden="true" />,
    members: <IconUsers size={18} aria-hidden="true" />,
    resources: <IconServer size={18} aria-hidden="true" />,
    billing: <IconReceipt size={18} aria-hidden="true" />,
    activity: <IconActivity size={18} aria-hidden="true" />,
    danger: <IconAlertTriangle size={18} aria-hidden="true" />,
};

export default function ProjectDetailPage() {
    const { projectId } = useParams<{ projectId: string }>();
    const { t } = useTranslation('projects');
    const viewerId = useAppSelector((state) => state.auth.user?.id);
    const [searchParams, setSearchParams] = useSearchParams();
    const [focusSection, setFocusSection] = useState<ResourceSectionKey>();
    const {
        data: project,
        isPending,
        isError,
        error,
        refetch,
        isRefetching,
    } = useProjectQuery(projectId);
    const notFound = getErrorCode(error) === 'project_not_found';
    const headingText =
        project?.name ??
        (isError ? (notFound ? t('detail.notFoundTitle') : t('detail.errorTitle')) : undefined);
    const headingRef = usePageHeading(headingText, t('list.title'));

    const tabs = project ? projectTabsFor(project, viewerId) : [];
    const activeTab = project ? parseProjectTab(searchParams.get('tab'), tabs) : 'general';

    const changeTab = useCallback(
        (next: string | null) => {
            if (!next) return;
            setSearchParams(
                (previous) => {
                    const params = new URLSearchParams(previous);
                    if (next === 'general') params.delete('tab');
                    else params.set('tab', next);
                    return params;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    const showResources = useCallback(
        (section: ResourceSectionKey) => {
            setFocusSection(section);
            changeTab('resources');
        },
        [changeTab]
    );
    const clearFocusSection = useCallback(() => setFocusSection(undefined), []);

    if (isPending) {
        return (
            <div className="w-full min-h-full flex flex-col justify-start items-start gap-8">
                <ProjectBreadcrumbs trail={[{ title: t('detail.loadingCrumb') }]} />
                <p role="status" aria-busy="true" className="text-sm text-fg/78">
                    {t('detail.loading')}
                </p>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="w-full min-h-full flex flex-col justify-start items-start gap-4">
                <ProjectBreadcrumbs trail={[{ title: headingText ?? '' }]} />
                <h1 ref={headingRef} tabIndex={-1} className="text-3xl font-bold">
                    {headingText}
                </h1>
                <p className="max-w-[80ch] text-base text-fg/78">
                    {notFound ? t('detail.notFoundMessage') : t('detail.errorMessage')}
                </p>
                <div className="flex flex-wrap gap-2">
                    {!notFound && (
                        <Button size="lg" onClick={() => refetch()} isLoading={isRefetching}>
                            {t('common.retry')}
                        </Button>
                    )}
                    <Link
                        to="/projects"
                        className="min-h-11 inline-flex items-center px-3 rounded-md border-[0.5px] border-line text-sm font-medium hover:bg-fg/5"
                    >
                        {t('detail.backToProjects')}
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full min-h-full flex flex-col justify-start items-center gap-8">
            <div className="w-full flex flex-col justify-start items-start">
                <ProjectBreadcrumbs trail={[{ title: project.name }]} />
                <div className="w-full flex flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                        <h1
                            ref={headingRef}
                            tabIndex={-1}
                            className="text-3xl font-bold break-words min-w-0"
                        >
                            {project.name}
                        </h1>
                        {project.isDefault && <TextBadge>{t('switcher.default')}</TextBadge>}
                        <TextBadge>
                            {t('detail.yourRole', { role: t(`roles.${project.role}`) })}
                        </TextBadge>
                    </div>
                    <p className="max-w-[80ch] text-base text-fg/68 break-words">
                        {project.description || t('detail.ownedBy', { name: project.owner.name })}
                    </p>
                </div>
            </div>

            <Tabs
                value={activeTab}
                onChange={changeTab}
                color="var(--fg)"
                keepMounted={false}
                className="w-full space-y-4"
            >
                <Tabs.List
                    aria-label={t('detail.tabsLabel')}
                    className="flex-nowrap overflow-x-auto"
                >
                    {tabs.map((tab) => (
                        <Tabs.Tab key={tab} value={tab} leftSection={TAB_ICONS[tab]}>
                            {t(`tabs.${tab}`)}
                        </Tabs.Tab>
                    ))}
                </Tabs.List>

                <Tabs.Panel value="general" tabIndex={0} className="w-full">
                    <GeneralTabPanel project={project} />
                </Tabs.Panel>
                <Tabs.Panel value="members" tabIndex={0} className="w-full">
                    <MembersTabPanel project={project} viewerId={viewerId} />
                </Tabs.Panel>
                <Tabs.Panel value="resources" tabIndex={0} className="w-full">
                    <ResourcesTabPanel
                        project={project}
                        focusSection={focusSection}
                        onSectionFocused={clearFocusSection}
                    />
                </Tabs.Panel>
                {tabs.includes('billing') && (
                    <Tabs.Panel value="billing" tabIndex={0} className="w-full">
                        <BillingTabPanel project={project} viewerId={viewerId} />
                    </Tabs.Panel>
                )}
                <Tabs.Panel value="activity" tabIndex={0} className="w-full">
                    <ActivityTabPanel projectId={project.id} />
                </Tabs.Panel>
                <Tabs.Panel value="danger" tabIndex={0} className="w-full">
                    <DangerZoneTabPanel project={project} onShowResources={showResources} />
                </Tabs.Panel>
            </Tabs>
        </div>
    );
}
